import { type KeyboardEvent } from 'react';

import { type IResultAnalyticsData } from '../../../../../interface/state/IElectionState';
import { CopyValue } from '../../../../utils/copy';
import { CoverageChip } from '../components';
import { getUploadedLabel, type PuStatus, puStatus, type RowCoverage } from '../utils';
import Pager from './Pager';

interface IKanbanViewProps {
	rows: IResultAnalyticsData[];
	onSelectChild: (item: IResultAnalyticsData) => void;
	coverage: Map<string, RowCoverage>;
	coverageLoading: boolean;
	currentPage: number;
	totalPages: number;
	onPage: (page: number) => void;
}

const COLUMNS: { key: PuStatus; label: string }[] = [
	{ key: 'uploaded', label: 'Uploaded' },
	{ key: 'pending', label: 'Pending' },
	{ key: 'conflict', label: 'Conflict' },
];

const KanbanCard = ({
	item,
	coverage,
	coverageLoading,
	onSelect,
}: {
	item: IResultAnalyticsData;
	coverage?: RowCoverage;
	coverageLoading: boolean;
	onSelect: () => void;
}) => {
	const uploadedLabel = getUploadedLabel(item);
	return (
		<div
			className='qa-kanban__card'
			role='button'
			tabIndex={0}
			aria-label={`View agent for ${item.code}`}
			onClick={onSelect}
			onKeyDown={(e: KeyboardEvent) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					onSelect();
				}
			}}
		>
			<span className='qa-kanban__codeline'>
				<span className='qa-kanban__code'>{item.code}</span>
				<CopyValue
					value={item.code}
					label='code'
					stopPropagation
				/>
			</span>
			<CoverageChip
				coverage={coverage}
				loading={coverageLoading}
			/>
			<span className={`qa-kanban__meta ${uploadedLabel ? '' : 'qa-kanban__meta--muted'}`}>
				{uploadedLabel ? `Uploaded ${uploadedLabel}` : 'Awaiting upload'}
			</span>
		</div>
	);
};

/** Read-only status board: one column per upload status. Card click opens coverage. */
const KanbanView = ({ rows, onSelectChild, coverage, coverageLoading, currentPage, totalPages, onPage }: IKanbanViewProps) => {
	const buckets: Record<PuStatus, IResultAnalyticsData[]> = { uploaded: [], pending: [], conflict: [] };
	rows.forEach((row) => buckets[puStatus(row)].push(row));

	return (
		<>
			<div className='qa-kanban'>
				{COLUMNS.map((col) => (
					<section
						key={col.key}
						className={`qa-kanban__col qa-kanban__col--${col.key}`}
						aria-label={`${col.label} (${buckets[col.key].length})`}
					>
						<header className='qa-kanban__colhead'>
							<span
								className='qa-kanban__coldot'
								aria-hidden='true'
							/>
							<span className='qa-kanban__coltitle'>{col.label}</span>
							<span className='qa-kanban__count'>{buckets[col.key].length}</span>
						</header>
						<div className='qa-kanban__list'>
							{buckets[col.key].length ? (
								buckets[col.key].map((item) => (
									<KanbanCard
										key={item.id}
										item={item}
										coverage={coverage.get(item.code)}
										coverageLoading={coverageLoading}
										onSelect={() => onSelectChild(item)}
									/>
								))
							) : (
								<p className='qa-kanban__empty'>Nothing here.</p>
							)}
						</div>
					</section>
				))}
			</div>
			<Pager
				currentPage={currentPage}
				totalPages={totalPages}
				onPage={onPage}
				showPageNote={false}
			/>
		</>
	);
};

export default KanbanView;
